import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';

interface ClientCard {
  linkId: number;
  orgId: number;
  orgName: string;
  addedAt: string;
  isNew: boolean;
}

export default function InstallerHome() {
  const navigate = useNavigate();
  const { user, token, currentMembership } = useAuth();
  const { t, locale } = useI18n();
  const [clients, setClients] = useState<ClientCard[]>([]);
  const [loading, setLoading] = useState(true);

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/my-clients`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setClients(data.clients);
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('team_home_morning');
    if (h < 18) return t('team_home_afternoon');
    return t('team_home_evening');
  })();

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Welcome banner — clean white, no dark gradient */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{greeting}, {user?.firstName || 'Pascal'}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {currentMembership?.orgName} · {t('team_home_partner')}
            </p>
          </div>
          <div className="text-right text-xs text-slate-400">
            {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Quick nav cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button onClick={() => navigate('/my-clients')} className="bg-white rounded-lg border border-slate-200 p-5 text-left hover:border-blue-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">{t('team_clients_my')}</h3>
              <p className="text-xs text-slate-500">{t(clients.length !== 1 ? 'team_home_clients_other' : 'team_home_clients_one', { n: clients.length })}</p>
            </div>
          </div>
        </button>
        <button onClick={() => navigate('/members')} className="bg-white rounded-lg border border-slate-200 p-5 text-left hover:border-blue-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-md bg-violet-100 text-violet-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">{t('team_home_myTeam')}</h3>
              <p className="text-xs text-slate-500">{t('team_home_myTeamSub')}</p>
            </div>
          </div>
        </button>
        <button onClick={() => navigate('/wifi-provisioning')} className="bg-white rounded-lg border border-slate-200 p-5 text-left hover:border-blue-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-md bg-teal-100 text-teal-600 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" /></svg>
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">{t('team_home_pairing')}</h3>
              <p className="text-xs text-slate-500">{t('team_home_pairingSub')}</p>
            </div>
          </div>
        </button>
      </div>

      {/* Recent clients */}
      {!loading && clients.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">{t('team_home_recentClients')}</h2>
            <button onClick={() => navigate('/my-clients')} className="text-xs text-blue-600 hover:text-blue-800 font-medium">{t('team_home_viewAll')} →</button>
          </div>
          <div className="divide-y divide-slate-100">
            {clients.slice(0, 5).map(client => (
              <button
                key={client.linkId}
                onClick={() => navigate(`/clients/${client.orgId}/dashboard`)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-blue-50/50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {client.orgName.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{client.orgName}</p>
                  <p className="text-xs text-slate-400">{t('team_home_addedOn', { date: new Date(client.addedAt).toLocaleDateString(locale) })}</p>
                </div>
                {client.isNew && <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded">{t('team_clients_new')}</span>}
                <span className="text-xs text-blue-600 font-medium">{t('team_home_open')} →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && clients.length === 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-10 text-center">
          <svg className="w-10 h-10 mx-auto text-slate-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
          </svg>
          <h3 className="text-sm font-medium text-slate-800">{t('team_clients_emptyTitle')}</h3>
          <p className="text-xs text-slate-500 mt-1">{t('team_home_emptyText')}</p>
        </div>
      )}
    </div>
  );
}
